import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Hands-free voice for the concierge, using the browser's own speech engines.
 *
 * WHY THIS EXISTS ALONGSIDE THE PHONE CHANNEL
 * The PSTN path (Twilio ConversationRelay) needs a number, a webhook and a
 * regulatory bundle. This needs nothing: SpeechRecognition for listening,
 * speechSynthesis for talking, and the SAME /concierge/chat endpoint the text
 * widget already uses — so it exercises the same brain, tools and guardrails.
 *
 * It's also a feature in its own right — "talk to us" on a booking page, which
 * neither Fresha nor Booksy offer — and it's the only way to test the
 * conversation end-to-end without touching infrastructure.
 *
 * WHAT IT DOES NOT PROVE
 * Telephony latency, Twilio's speech recognition, and barge-in over a real line
 * all behave differently. This validates the CONVERSATION; it doesn't validate
 * the phone call.
 *
 * THE ONE NON-OBVIOUS BUG THIS AVOIDS
 * If the microphone stays open while the assistant speaks, it hears itself,
 * transcribes its own voice, and answers its own question — an infinite loop
 * that also bills a reply per turn. Recognition is therefore STOPPED for the
 * whole time we're speaking and only restarted once the utterance ends.
 */

function getRecognition() {
    const Ctor = typeof window !== 'undefined'
        && (window.SpeechRecognition || window.webkitSpeechRecognition);
    if (!Ctor) return null;
    const r = new Ctor();
    r.lang = 'en-GB';
    // continuous:false gives us one utterance per turn, which matches a
    // conversation better than a rolling stream and avoids half-sentences being
    // sent to the brain.
    r.continuous = false;
    r.interimResults = true;
    r.maxAlternatives = 1;
    return r;
}

export const voiceSupported = () =>
    typeof window !== 'undefined'
    && !!(window.SpeechRecognition || window.webkitSpeechRecognition)
    && !!window.speechSynthesis;

/**
 * @param {(text:string)=>Promise<void>} onUtterance called with each final transcript
 * @param {boolean} busy true while the brain is thinking — we don't listen then
 */
export default function useVoiceChat({ onUtterance, busy }) {
    const [active, setActive] = useState(false);
    const [listening, setListening] = useState(false);
    const [speaking, setSpeaking] = useState(false);
    const [partial, setPartial] = useState('');
    const [error, setError] = useState('');

    const recRef = useRef(null);
    const activeRef = useRef(false);
    const speakingRef = useRef(false);
    // Guards against restarting recognition after unmount/stop.
    const stoppedRef = useRef(true);

    const stopListening = useCallback(() => {
        const r = recRef.current;
        if (!r) return;
        try { r.abort(); } catch (e) { /* already stopped */ }
        setListening(false);
    }, []);

    const startListening = useCallback(() => {
        if (stoppedRef.current || speakingRef.current || busy) return;
        const r = recRef.current;
        if (!r) return;
        try {
            r.start();
            setListening(true);
        } catch (e) {
            // start() throws if it's already running — harmless.
        }
    }, [busy]);

    /** Speak text, keeping the mic shut until it's finished. */
    const speak = useCallback((text) => {
        if (!text || !window.speechSynthesis) return;
        stopListening();
        speakingRef.current = true;
        setSpeaking(true);

        const u = new window.SpeechSynthesisUtterance(String(text));
        u.lang = 'en-GB';
        u.rate = 1.05;   // a shade quicker than default; default sounds sluggish
        const done = () => {
            speakingRef.current = false;
            setSpeaking(false);
            // Back to listening, so the client can just keep talking.
            if (!stoppedRef.current) setTimeout(startListening, 150);
        };
        u.onend = done;
        u.onerror = done;

        try {
            window.speechSynthesis.cancel();   // drop anything queued
            window.speechSynthesis.speak(u);
        } catch (e) {
            done();
        }
    }, [startListening, stopListening]);

    const stop = useCallback(() => {
        stoppedRef.current = true;
        activeRef.current = false;
        setActive(false);
        setPartial('');
        stopListening();
        try { window.speechSynthesis && window.speechSynthesis.cancel(); } catch (e) { /* noop */ }
        speakingRef.current = false;
        setSpeaking(false);
    }, [stopListening]);

    const start = useCallback(() => {
        if (!voiceSupported()) {
            setError('This browser cannot do voice. Try Chrome, or type instead.');
            return;
        }
        setError('');
        stoppedRef.current = false;
        activeRef.current = true;
        setActive(true);

        if (!recRef.current) {
            const r = getRecognition();
            if (!r) {
                setError('This browser cannot do voice. Try Chrome, or type instead.');
                return;
            }

            r.onresult = (ev) => {
                let finalText = '';
                let interim = '';
                for (let i = ev.resultIndex; i < ev.results.length; i += 1) {
                    const res = ev.results[i];
                    if (res.isFinal) finalText += res[0].transcript;
                    else interim += res[0].transcript;
                }
                setPartial(interim);
                const said = finalText.trim();
                if (said) {
                    setPartial('');
                    setListening(false);
                    onUtterance(said);
                }
            };

            r.onerror = (ev) => {
                // 'no-speech' and 'aborted' are routine, not failures worth
                // showing — the caller just paused, or we stopped the mic to talk.
                if (ev.error === 'not-allowed' || ev.error === 'service-not-allowed') {
                    setError('Microphone blocked. Allow access to talk.');
                    stop();
                    return;
                }
                setListening(false);
            };

            r.onend = () => {
                setListening(false);
                // Recognition ends after every utterance; restart unless we're
                // speaking, thinking, or shut down.
                if (!stoppedRef.current && !speakingRef.current) {
                    setTimeout(startListening, 200);
                }
            };

            recRef.current = r;
        }

        startListening();
    }, [onUtterance, startListening, stop]);

    // Resume listening once the brain has finished thinking.
    useEffect(() => {
        if (!busy && activeRef.current && !speakingRef.current && !stoppedRef.current) {
            startListening();
        }
    }, [busy, startListening]);

    // Never leave a hot mic or a talking page behind.
    useEffect(() => () => {
        stoppedRef.current = true;
        try { recRef.current && recRef.current.abort(); } catch (e) { /* noop */ }
        try { window.speechSynthesis && window.speechSynthesis.cancel(); } catch (e) { /* noop */ }
    }, []);

    return { active, listening, speaking, partial, error, start, stop, speak };
}
